import useBundleTranslation from 'i18n';
import useCustomSimplifiedForm from 'components/common/form/hooks/useCustomSimplifiedForm';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BookmarkType } from 'components/external-reference/bookmark/BookmarkDropdown';
import useBookmarks from 'components/external-reference/hooks/useBookmarks';
import LoadingPlaceholder from 'components/common/loading-placeholder/LoadingPlaceholder';
import { Box, FormControl, Skeleton, Stack } from '@mui/material';
import ReactHookFormController, { TemplateFormComponent } from 'components/common/form/layout/ReactHookFormController';
import BookmarkManager from 'components/external-reference/bookmark/BookmarManager';
import { prepareFormElementProps } from 'components/common/form/formTools';
import MapDataForm from 'components/common/grid/cell/burst-filter-cell/MapDataForm';
import useMapData from 'components/common/grid/cell/burst-filter-cell/useMapData';
import { IFiltersFormProps } from './index';
import SelectionErrorMessage from './SelectionErrorMessage';
import useReportFiltersForReference from 'app/report/useReportFiltersForReference';
import {
    ComponentSettingsManageFilters,
    ContentSettings,
    IKpiAlertRule,
    ManageFiltersInitialDataResponse,
} from '@report-content';
import WrapperFormLabel from 'components/common/form/layout/helper/WrapperFormLabel';
import { FormComponentValueSelectable } from 'components/common/form/layout/control';
import { useQuery } from '@tanstack/react-query';
import { instance } from 'api/api';
import FormStaticLabel from 'components/common/form/layout/static/FormStaticLabel';
import { RawFormComponentType } from 'components/common/form';

export default function InternalReportFiltersForm({
    defaultData,
    formValues,
    elementId,
    segmentValueId,
    externalId,
    setForm,
    manageFiltersSettings,
    manageFiltersInitialData,
    contentSettings,
    segmentData,
    alertRuleSelectionError,
}: IFiltersFormProps & {
    manageFiltersSettings: ComponentSettingsManageFilters & { bookmarksList: BookmarkType[] };
    manageFiltersInitialData: ManageFiltersInitialDataResponse;
    contentSettings: ContentSettings;
}) {
    const { t } = useBundleTranslation(['components/notification/burst']);
    const elementProps = useCustomSimplifiedForm(defaultData);

    const overrideInd = elementProps.form.hookFormWatch('override_external_filters_ind') == 'Y';
    const externalMethod = elementProps.form.hookFormWatch('external_filters_usage_method');
    const mapId = elementProps.form.hookFormWatch('external_filters_user_map_id');
    const showAlertsList = elementProps.form.hookFormWatch('send_for_alert_rule') == 'selected';
    const showAlertsSection = elementProps.form.hookFormWatch('only_include_when_alert_ind') == 'Y';

    const [initialManageFiltersSettings] = useState(structuredClone(manageFiltersSettings));

    const initialFilters = useReportFiltersForReference(
        contentSettings.segmentValues,
        contentSettings.segmentValueId,
        initialManageFiltersSettings,
        manageFiltersInitialData,
    ).filters;

    const [selectedBookmark, setSelectedBookmark] = useState<BookmarkType | undefined>();
    const {
        bookmarks,
        APIUpdateBookmark,
        APIDeleteBookmark,
        APIDuplicateBookmark,
        APISortBookmarks,
        filtersData,
        APIIsLoading,
    } = useBookmarks(
        {
            bookmarksList: manageFiltersSettings.bookmarksList,
            filtersList: initialFilters,
            liveFilters: true,
            selectedBookmarkId: 0,
        },
        {
            elementId: elementId,
            elementType: 'internal report',
            segmentData: segmentData,
            distributionItemId: defaultData['notification_schedule_distribution_item_id'],
        },
    );

    const { filters } = useReportFiltersForReference(
        contentSettings.segmentValues,
        contentSettings.segmentValueId,
        manageFiltersSettings,
        manageFiltersInitialData,
    );

    const { secondColumn, isLoaded } = useMapData(externalId, elementId, mapId);
    const firstColumn = useMemo(
        () =>
            filters
                .filter((f) => !f.replacedBySegment)
                .map((f) => ({
                    label: f.title ?? f.name,
                    value: f.filterId,
                })),
        [filters],
    );

    const [mapActualState, setMapActualState] = useState(
        formValues?.map_data?.map((r: any) => ({
            firstColumn: r.filterColumn,
            secondColumn: r.mapColumn,
            data: r?.data ?? null,
        })) ?? [],
    );

    const [formReady, setFormReady] = useState(false);
    useEffect(() => {
        if (isLoaded) {
            setTimeout(() => {
                setForm(elementProps.form);
                setFormReady(true);
            }, 100);
        }
    }, [isLoaded]);

    const [mapData, setMapData] = useState<any>(null);
    const mapDataRef = useRef<(data: any) => void>();
    const [mapFormOpen, setMapFormOpen] = useState(false);
    const handleGearClick = (data: any, setData: (data: any) => void) => {
        setMapData(data);
        mapDataRef.current = setData;
        setMapFormOpen(true);
    };

    const { data: alertsData, isLoading } = useQuery<Array<IKpiAlertRule>, Error>({
        queryKey: ['alerts_for_burst', elementId, segmentValueId, externalId],
        queryFn: async () => {
            const response = await instance.get(`/data/editor/report/${elementId}/alert-rule-for-kpi-for-add`);
            return response.data.status == 'OK' ? response.data.data : [];
        },
        refetchOnMount: true,
    });

    const alertValues = useMemo<Array<FormComponentValueSelectable>>(() => {
        if (!alertsData) {
            return [];
        }
        return alertsData.map((alertRule) => ({
            label: alertRule.name,
            value: String(alertRule.report_alert_rule_id),
        })) as FormComponentValueSelectable[];
    }, [alertsData]);

    return (
        <>
            {(!formReady || APIIsLoading) && <LoadingPlaceholder />}
            {mapFormOpen && (
                <MapDataForm
                    isOpen={mapFormOpen}
                    mapData={mapData}
                    mapDataCallback={(data) => {
                        //@ts-ignore
                        mapDataRef.current(data);
                        setMapFormOpen(false);
                    }}
                />
            )}
            <Stack spacing={2}>
                <Box>
                    <TemplateFormComponent
                        elementProps={elementProps}
                        component={'FormCheckbox'}
                        name={'only_include_when_alert_ind'}
                        label={t('filter.only_include_when_alert_ind')}
                    />
                </Box>
                <Box className={showAlertsSection ? '' : 'd-none'} sx={{ width: '264px' }}>
                    <WrapperFormLabel>{t('filter.send_for_alert_rule')}</WrapperFormLabel>
                    <TemplateFormComponent
                        elementProps={elementProps}
                        component={'FormToggleButtonGroup'}
                        props={{ width: '100%' }}
                        componentValues={[
                            { label: t('filter.any_alert_rule'), value: 'any' },
                            { label: t('filter.selected_alert_rule'), value: 'selected' },
                        ]}
                        name={'send_for_alert_rule'}
                    />
                </Box>
                <Stack className={showAlertsList && showAlertsSection ? '' : 'd-none'} spacing={1}>
                    <Box sx={{ flexGrow: 1 }}>
                        <FormStaticLabel
                            elementProps={prepareFormElementProps({
                                form: elementProps.form,
                                component: {
                                    label: t('alert_rules'),
                                    props: { isSectionLabel: true },
                                } as RawFormComponentType,
                            })}
                        />
                    </Box>
                    <Box>
                        {isLoading ? (
                            <Skeleton height={'75px'} animation={'pulse'} />
                        ) : (
                            <TemplateFormComponent
                                component={'CheckBoxList'}
                                elementProps={elementProps}
                                name={'selected_alert_rules'}
                                props={{
                                    data: { values: alertValues },
                                    showCounter: false,
                                    // selectAllLabel: t('alert_rules'),
                                    async: false,
                                    showSearch: false,
                                    props: { emptyHeight: 75 },
                                }}
                            />
                        )}
                    </Box>
                    {!isLoading && alertRuleSelectionError && (
                        <SelectionErrorMessage label={t('filter.error_select_at_least_one_alert_rule')} />
                    )}
                </Stack>
                <Box>
                    <TemplateFormComponent
                        elementProps={elementProps}
                        component={'FormCheckbox'}
                        name={'override_external_filters_ind'}
                        label={t('filter.custom_burst')}
                    />
                </Box>
                <Stack className={overrideInd ? '' : 'd-none'}>
                    <Stack direction={'row'} spacing={2}>
                        <Box sx={{ width: '264px' }}>
                            <WrapperFormLabel>{t('filter.apply_filters')}</WrapperFormLabel>
                            <TemplateFormComponent
                                elementProps={elementProps}
                                component={'FormToggleButtonGroup'}
                                componentValues={[
                                    { label: t('manual'), value: 'standard' },
                                    { label: t('via_user_map'), value: 'user map' },
                                ]}
                                props={{ width: '100%' }}
                                name={'external_filters_usage_method'}
                            />
                        </Box>
                        <Box sx={{ flexGrow: 1 }} className={externalMethod == 'user map' ? '' : 'd-none'}>
                            <FormControl fullWidth>
                                <WrapperFormLabel>{t('user_map')}</WrapperFormLabel>
                                <TemplateFormComponent
                                    elementProps={elementProps}
                                    component={'FormSelect'}
                                    componentValues={formValues.external_filters_user_map_id}
                                    name={'external_filters_user_map_id'}
                                />
                            </FormControl>
                        </Box>
                    </Stack>

                    <Box sx={{ marginTop: '17px;' }} className={externalMethod == 'user map' ? '' : 'd-none'}>
                        <>
                            {isLoaded && formReady ? (
                                <TemplateFormComponent
                                    name={'map-data'}
                                    elementProps={elementProps}
                                    component={'SelectMap'}
                                    props={{
                                        data: {
                                            handleGearClick: handleGearClick,
                                            uniqueRule: 'both',
                                            firstColumn: {
                                                label: t('filter.filter'),
                                                source: {
                                                    type: 'manual',
                                                    manualData: firstColumn,
                                                },
                                            },
                                            secondColumn: {
                                                label: t('filter.user_map_column'),
                                                source: {
                                                    type: 'manual',
                                                    manualData: secondColumn,
                                                },
                                            },
                                            actualValues: mapActualState,
                                        },
                                    }}
                                />
                            ) : (
                                <LoadingPlaceholder />
                            )}
                        </>
                    </Box>

                    <Box sx={{ marginTop: '17px;' }} className={externalMethod == 'standard' ? '' : 'd-none'}>
                        <BookmarkManager
                            managerContextType={'combination'}
                            setOnEditMode={() => {}}
                            bookmarkForEdit={selectedBookmark}
                            elementId={elementId}
                            segmentValueId={segmentValueId}
                            bookmarks={bookmarks.filter((b) => b.bookmarkType == 'combination')}
                            filters={filters}
                            siblingTabs={[]}
                            onDeleteClick={APIDeleteBookmark}
                            onDuplicateClick={APIDuplicateBookmark}
                            onSortEnd={APISortBookmarks}
                            onUpdateClick={(bookmark: BookmarkType) => {
                                APIUpdateBookmark(bookmark, 0);
                                if (selectedBookmark) {
                                    //handleHideManager();
                                }
                            }}
                        />
                    </Box>
                </Stack>

                <Box sx={{ marginTop: '17px;' }} className={externalMethod == 'user map' ? '' : 'd-none'}>
                    <FormControl>
                        <ReactHookFormController
                            elementProps={prepareFormElementProps({
                                ...elementProps,
                                inputFilter: 'int',
                                component: {
                                    uid: 'max_images_in_email',
                                    name: 'max_images_in_email',
                                    component: 'FormText',
                                },
                                componentProps: {
                                    label: t('filter.max_inst'),
                                },
                            })}
                        />
                    </FormControl>
                </Box>
            </Stack>
        </>
    );
}
